import React from 'react';
import { Building2, MapPin } from 'lucide-react';

interface ProfileCardInfoProps {
  fullName: string;
  jobTitle: string;
  department: string;
  location: string;
  bio: string;
  variant: 'preview' | 'full';
  t: (key: string) => string;
}

const ProfileCardInfo: React.FC<ProfileCardInfoProps> = ({
  fullName,
  jobTitle,
  department,
  location,
  bio,
  variant,
  t
}) => {
  const isPreview = variant === 'preview';

  if (isPreview) {
    // Tight stack — keeps the info column shorter than the 56px avatar so
    // `flex items-end` doesn't drag the avatar below the header. Three rows
    // stay readable but cap at ~50px total.
    return (
      <>
        {/* Name */}
        <div className="flex items-center gap-1.5 leading-tight">
          <h3 className="text-sm font-bold text-foreground drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)] truncate">
            {fullName || t('Your Name')}
          </h3>
        </div>
        {/* Job title pill */}
        <span className="inline-block text-[11px] text-muted-foreground px-1.5 py-px bg-white/80 backdrop-blur-sm rounded leading-tight mt-0.5">
          {jobTitle || t('Job Title')}
        </span>
        {/* Department + location pill */}
        {(department || location) && (
          <div className="flex items-center text-[10px] text-muted-foreground gap-x-1 px-1.5 py-px bg-white/70 backdrop-blur-sm rounded w-fit leading-tight mt-0.5">
            {department && (
              <>
                <Building2 className="w-3 h-3" />
                <span className="truncate">{department}</span>
              </>
            )}
            {department && location && <span>•</span>}
            {location && (
              <>
                <MapPin className="w-3 h-3" />
                <span className="truncate">{location}</span>
              </>
            )}
          </div>
        )}
      </>
    );
  }

  // Full variant - matches PublicProfileDisplay exactly
  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        <h1 className="text-3xl font-bold text-foreground drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]">
          {fullName}
        </h1>
      </div>
      <span className="inline-block text-lg text-muted-foreground mb-1 px-3 py-1 bg-white/80 backdrop-blur-sm rounded-lg">
        {jobTitle}
      </span>
      <div className="flex items-center text-muted-foreground mb-3 px-3 py-1 bg-white/70 backdrop-blur-sm rounded-lg w-fit">
        {department && (
          <>
            <Building2 className="w-4 h-4 me-2" />
            <span>{department}</span>
            {location && <span className="mx-2">•</span>}
          </>
        )}
        {location && (
          <>
            <MapPin className="w-4 h-4 me-2" />
            <span>{location}</span>
          </>
        )}
      </div>

      {/* Bio */}
      {bio && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-2 uppercase tracking-wide">{t('profileBio')}</h3>
          <p dir="auto" className="text-muted-foreground leading-relaxed">{bio}</p>
        </div>
      )}
    </>
  );
};

export default ProfileCardInfo;
